// ✅ src/pages/VehicleV2/VehicleTdsPercentListing.jsx  (FULL FILE)
// Listing page for Vehicle TDS Percent
//
// Backend mount:
//   app.use("/api/tdsPercentListing", tdsPercentListing);
//
// APIs used:
//   GET  https://nissi-india.com/api/tdsPercentListing/list?page=1&limit=10&search=..
//   POST https://nissi-india.com/api/tdsPercentListing/delete
//
// Navigation:
//   Edit -> /vehicle/VehicleTDSPercent?tdsPercentId=123
//   Add  -> /vehicle/VehicleTDSPercent

import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaSearch, FaFilter, FaEdit, FaTrash } from "react-icons/fa";
import axios from "axios";

const API_BASE = "https://nissi-india.com/api/tdsPercentListing";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === undefined || v === null) return def;
  return String(v).trim();
};

const ddmmyyyyToDisplay = (value) => {
  if (!value) return "";
  const s = String(value).trim();

  // already dd/MM/yyyy
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;

  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;

  return d.toLocaleDateString("en-GB");
};

const VehicleTdsPercentListing = () => {
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ Shared localStorage / sessionStorage pattern
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || localStorage.getItem("branchid") || "";
  const branch = localStorage.getItem("branchType") || localStorage.getItem("branchtype") || "";
  const userId = localStorage.getItem("userId") || localStorage.getItem("loginuserid") || "";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  // optional rights / branch active pattern
  const canDelete = useMemo(() => {
    const v = sessionStorage.getItem("tdspercent_deleterights");
    if (v === null) return true;
    return v === "1" || v === "true";
  }, []);

  const branchActive = useMemo(() => {
    const v = sessionStorage.getItem("BranchActive") || localStorage.getItem("BranchActive");
    if (v === null) return true;
    return v === "1" || v === "true";
  }, []);

  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showFilters, setShowFilters] = useState(true);

  const [rows, setRows] = useState([]);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark ? "bg-gray-700/30 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  /* ---------------- Fetch List ---------------- */
  const fetchList = useCallback(
    async (pageNo = page, pageSize = limit, searchText = search) => {
      try {
        setLoading(true);

        const { data } = await axios.get(`${API_BASE}/list`, {
          params: {
            page: pageNo,
            limit: pageSize,
            search: toStr(searchText),
            loginUserId,
          },
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load data");
        }

        setRows(Array.isArray(data?.rows) ? data.rows : []);
        setTotal(toInt(data?.totalRecords ?? data?.total, 0));
        setTotalPages(toInt(data?.totalPages, 1) || 1);

        const nextPage = toInt(data?.currentPage ?? data?.page, pageNo);
        const nextLimit = toInt(data?.pageSize ?? data?.limit, pageSize);

        setPage(nextPage);
        setLimit(nextLimit);
      } catch (err) {
        console.error(err);
        toast.error(err?.response?.data?.message || err?.message || "Server error while loading list");
        setRows([]);
        setTotal(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    },
    [page, limit, search, loginUserId, commonHeaders]
  );

  useEffect(() => {
    fetchList(1, limit, search);
  }, [fetchList, limit, search]);

  /* ---------------- Checkbox logic ---------------- */
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
    } else {
      setSelectedIds(rows.map((r) => r.tdsPercentId));
    }
    setSelectAll(!selectAll);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  useEffect(() => {
    if (!rows.length) {
      setSelectAll(false);
      return;
    }
    const allSelected = rows.every((r) => selectedIds.includes(r.tdsPercentId));
    setSelectAll(allSelected);
  }, [rows, selectedIds]);

  /* ---------------- Delete ---------------- */
  const handleDelete = async () => {
    if (!branchActive) {
      toast.error("Branch is not active. Delete not allowed.");
      return;
    }

    if (!canDelete) {
      toast.error("You do not have rights to delete TDS percent.");
      return;
    }

    if (selectedIds.length === 0) {
      toast.warn("Please select any one...");
      return;
    }

    if (!window.confirm("Are you sure to delete?")) {
      return;
    }

    try {
      setDeleteLoading(true);

      const { data } = await axios.post(
        `${API_BASE}/delete`,
        {
          ids: selectedIds,
          loginUserId,
        },
        {
          headers: commonHeaders,
        }
      );

      if (!data?.success && !data?.message) {
        throw new Error("Delete failed");
      }

      if (data?.success) {
        toast.success(data?.message || "Deleted successfully");
      } else {
        toast.error(data?.message || "Delete failed");
      }

      const deletingAllRowsOfPage = rows.length === selectedIds.length;
      const nextPage = page > 1 && deletingAllRowsOfPage ? page - 1 : page;

      setSelectedIds([]);
      setSelectAll(false);

      await fetchList(nextPage, limit, search);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Delete failed");
    } finally {
      setDeleteLoading(false);
    }
  };

  /* ---------------- Edit / Add ---------------- */
  const handleEdit = (tdsPercentId) => {
    navigate(`/vehicle/VehicleTDSPercent?tdsPercentId=${tdsPercentId}`);
  };

  const handleAddNew = () => {
    navigate(`/vehicle/VehicleTDSPercent`);
  };

  const handleClearFilters = () => {
    setSearch("");
    setPage(1);
    setSelectedIds([]);
    setSelectAll(false);
  };

  /* ---------------- Pagination ---------------- */
  const goToPage = (p) => {
    const newPage = Math.max(1, Math.min(totalPages, p));
    setSelectedIds([]);
    setSelectAll(false);
    fetchList(newPage, limit, search);
  };

  const handleLimitChange = (e) => {
    const newLimit = Number(e.target.value || 10);
    setSelectedIds([]);
    setSelectAll(false);
    fetchList(1, newLimit, search);
  };

  const pageButtons = useMemo(() => {
    const btns = [];
    const maxToShow = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxToShow - 1);
    start = Math.max(1, end - maxToShow + 1);

    for (let p = start; p <= end; p++) {
      btns.push(p);
    }
    return btns;
  }, [page, totalPages]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE TDS PERCENT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black`
          }`}
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-8 flex-wrap gap-4">
            <div className="flex items-center gap-4 flex-wrap">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Vehicle TDS Percent Listing
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {total} Records
              </span>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                type="button"
                onClick={handleAddNew}
                className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-4 py-2 rounded transition-colors`}
              >
                <FaPlus className="w-4 h-4" />
                Add New
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div
              className={`rounded-xl p-6 mb-6 border ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FaFilter /> FILTRATION AREA
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className={labelClass}>Search</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaSearch className="text-gray-400" />
                    </div>

                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Type to search vehicle subtype / percent / date..."
                      className={`pl-10 ${inputClass}`}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium transition-colors duration-200"
                >
                  Clear All Filters
                </button>

                <button
                  type="button"
                  onClick={() => fetchList(1, limit, search)}
                  className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200`}
                >
                  <FaSearch />
                  Search Records
                </button>
              </div>
            </div>
          )}

          {/* Results */}
          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {loading ? (
              <div className="text-center py-16">
                <h3 className={`text-lg font-semibold ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  Loading...
                </h3>
              </div>
            ) : rows.length === 0 ? (
              <div className="text-center py-16">
                <div
                  className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                    isDark ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <FaSearch className="w-10 h-10 text-gray-400" />
                </div>

                <h3 className={`text-xl font-semibold mb-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  No Records Found
                </h3>

                <p className={`mb-6 max-w-sm mx-auto ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Try adjusting your search or add a new TDS percent.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-4 text-center font-semibold">S.No</th>
                      <th className="px-4 py-4 text-center font-semibold">Date</th>
                      <th className="px-4 py-4 text-center font-semibold">Vehicle Type</th>
                      <th className="px-4 py-4 text-center font-semibold">TDS Percent</th>
                      <th className="px-4 py-4 text-center font-semibold">Edit</th>
                      <th className="px-4 py-4 text-center font-semibold">
                        <div className="flex items-center justify-center">
                          Select All
                          <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleSelectAll}
                            className="ml-2"
                          />
                        </div>
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {rows.map((r, idx) => (
                      <tr
                        key={r.tdsPercentId}
                        className={`hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200 ${
                          isDark ? "bg-gray-900" : "bg-white"
                        }`}
                      >
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white text-center">
                          {(page - 1) * limit + (idx + 1)}
                        </td>

                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">
                          {ddmmyyyyToDisplay(r.date)}
                        </td>

                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">
                          {r.vehicleSubTypeName || ""}
                        </td>

                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">
                          {r.tdsPercent ?? ""}
                        </td>

                        <td className="px-4 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => handleEdit(r.tdsPercentId)}
                            className="inline-flex items-center gap-1 px-3 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transform hover:-translate-y-0.5 transition-all duration-200 shadow-md hover:shadow-lg"
                          >
                            <FaEdit />
                            Edit
                          </button>
                        </td>

                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(r.tdsPercentId)}
                            onChange={() => toggleSelect(r.tdsPercentId)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mt-6">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
              Showing{" "}
              <b>
                {rows.length === 0 ? 0 : (page - 1) * limit + 1} - {(page - 1) * limit + rows.length}
              </b>{" "}
              of <b>{total}</b>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>Rows:</span>

              <select
                value={limit}
                onChange={handleLimitChange}
                className={`px-2 py-2 rounded border ${
                  isDark ? "bg-gray-800 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                }`}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>

              <button
                type="button"
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1}
                className={`px-3 py-2 rounded border ${
                  page <= 1
                    ? "opacity-50 cursor-not-allowed"
                    : isDark
                    ? "border-gray-600 hover:bg-gray-800 text-white"
                    : "border-gray-300 hover:bg-gray-50 text-gray-800"
                }`}
              >
                Prev
              </button>

              {pageButtons.map((p) => (
                <button
                  type="button"
                  key={p}
                  onClick={() => goToPage(p)}
                  className={`px-3 py-2 rounded border ${
                    p === page
                      ? `${buttonClass} text-white border-transparent`
                      : isDark
                      ? "border-gray-600 hover:bg-gray-800 text-white"
                      : "border-gray-300 hover:bg-gray-50 text-gray-800"
                  }`}
                >
                  {p}
                </button>
              ))}

              <button
                type="button"
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages}
                className={`px-3 py-2 rounded border ${
                  page >= totalPages
                    ? "opacity-50 cursor-not-allowed"
                    : isDark
                    ? "border-gray-600 hover:bg-gray-800 text-white"
                    : "border-gray-300 hover:bg-gray-50 text-gray-800"
                }`}
              >
                Next
              </button>
            </div>
          </div>

          {/* Delete Button */}
          <div className="flex justify-end mt-6">
            <button
              type="button"
              onClick={handleDelete}
              disabled={
                selectedIds.length === 0 || deleteLoading || !branchActive || !canDelete
              }
              className={`flex items-center gap-2 px-4 py-2 text-white text-sm rounded-lg shadow transition ${
                selectedIds.length === 0 || deleteLoading || !branchActive || !canDelete
                  ? "bg-gray-400 cursor-not-allowed"
                  : `${buttonClass} ${hoverClass}`
              }`}
            >
              {deleteLoading ? "Deleting..." : <><FaTrash /> Delete Selected</>}
            </button>
          </div>

          <div className={`mt-8 pt-6 border-t ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <p className={`text-sm text-center ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              INFRA SCREEN : VEHICLE TDS PERCENT LISTING
            </p>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleTdsPercentListing;